import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { CameraIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

interface StructureFormData {
  code: string;
  type: string;
  location: string;
  description?: string;
  size?: string;
  screenSize?: string;
  screenResolution?: string;
  powerConsumption?: string;
  direction?: string;
  isActive: boolean;
  isLED: boolean;
  alwaysAvailable?: boolean;
  notes?: string;
  file?: FileList | null;
  imageUrl?: string;
  latitude?: number | null;
  longitude?: number | null;
  dailyPrice?: number | string;
  monthlyPrice?: number | string;
}

const STRUCTURE_TYPES = [
    'LED Экран',
    'Ситиборд',
    'Мупи',
    'Услуга',
    'Другое'
];

const StructureForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  // Debug log
  console.log('StructureForm loaded - ID:', id, 'isEditMode:', isEditMode);
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  
  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm<StructureFormData>({
    defaultValues: {
      code: '',
      type: STRUCTURE_TYPES[0],
      location: '',
      description: '',
      size: '',
      screenSize: '',
      screenResolution: '',
      powerConsumption: '',
      direction: '',
      isActive: true,
      isLED: false,
      alwaysAvailable: false,
      notes: '',
      file: null,
      imageUrl: '',
      latitude: null,
      longitude: null,
      dailyPrice: '',
      monthlyPrice: '',
    }
  });
  
  const isLedWatch = watch('isLED');
  const imageFileWatch = watch('file');

  // Reset error state on component mount
  useEffect(() => {
    setError(null);
  }, []); 

  useEffect(() => {
    if (isEditMode) {
      setLoading(true);
      axios.get(`/structures/${id}`)
        .then(response => {
          const structure = response.data;
          reset({
              code: structure.code,
              type: structure.type,
              location: structure.location,
              description: structure.description || '',
              size: structure.size || '',
              screenSize: structure.screenSize || '',
              screenResolution: structure.screenResolution || '',
              direction: structure.direction || '',
              isActive: structure.isActive,
              isLED: structure.isLED,
              alwaysAvailable: structure.alwaysAvailable,
              powerConsumption: structure.powerConsumption || '',
              notes: structure.notes || '',
              imageUrl: structure.imageUrl || '',
              latitude: structure.latitude || null,
              longitude: structure.longitude || null,
              file: null
          });
          
          if (structure.imageUrl) {
            setImagePreview(structure.imageUrl);
            setCurrentImageUrl(structure.imageUrl);
          }
        })
        .catch(err => {
          console.error('Error fetching structure:', err);
          setError('Ошибка при загрузке данных конструкции');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, isEditMode, reset]);

  useEffect(() => {
      let fileReader: FileReader | null = null;
      let isCancel = false;
      if (imageFileWatch && imageFileWatch instanceof FileList && imageFileWatch.length > 0) {
          const currentFile = imageFileWatch[0];
          fileReader = new FileReader();
          fileReader.onload = (e) => {
              const result = e.target?.result;
              if (result && !isCancel) {
                  setImagePreview(result as string);
              }
          }
          fileReader.readAsDataURL(currentFile);
      } else {
          setImagePreview(currentImageUrl);
      }
      return () => {
          isCancel = true;
          if (fileReader && fileReader.readyState === 1) {
              fileReader.abort();
          }
      }
  }, [imageFileWatch, currentImageUrl]);

  const typeValue = watch('type');

  useEffect(() =>{
    setValue('isLED', typeValue === 'LED Экран');
  }, [typeValue, setValue]);

  const onSubmit = async (data: StructureFormData) => {
    setSubmitting(true);
    setError(null);
    
    const formData = new FormData();
    
    (Object.keys(data) as Array<keyof StructureFormData>).forEach((key) => {
        if (key === 'imageUrl' || key === 'file') return;

        const value = data[key];
        
        if ((key === 'latitude' || key === 'longitude') && value === null) {
             return;
        }

        if (key === 'dailyPrice' || key === 'monthlyPrice') {
            if (value !== undefined && value !== null && value !== '') {
                formData.append(key, String(value));
            }
            return;
        }

        if (value !== undefined && value !== null) {
            const appendValue = typeof value === 'boolean' ? String(value) : 
                              typeof value === 'number' ? String(value) : 
                              value; 
            formData.append(key, appendValue);
        }
    });

    if (data.file && data.file instanceof FileList && data.file.length > 0) {
        formData.append('file', data.file[0]);
    }
    
    console.log('Submitting FormData:');
    formData.forEach((value, key) => {
        console.log(`${key}: ${value}`);
    });

    try {
      let response;
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (isEditMode) {
        response = await axios.put(`/structures/${id}`, formData, config);
      } else {
        response = await axios.post('/structures', formData, config);
      }
      
      const structureId = response.data?.id || id;
      if (structureId) {
        navigate(`/structures/${structureId}`);
      } else {
          console.error('No ID found after structure save, navigating back to list');
          navigate('/structures');
      }

    } catch (err: any) {
      console.error('Error saving structure:', err);
      setError(err.response?.data?.message || 'Ошибка при сохранении конструкции');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title={isEditMode ? 'Редактировать Конструкцию' : 'Добавить Конструкцию'}
        backLink="/structures"
      />
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <fieldset className="grid grid-cols-1 gap-6 md:grid-cols-2">
             <legend className="text-lg font-medium text-gray-900 mb-2">Основная информация</legend>
            <div>
              <label htmlFor="code" className="form-label">
                Код конструкции *
              </label>
              <input
                id="code"
                type="text"
                className={`form-input ${errors.code ? 'border-red-500' : ''}`}
                placeholder="Например, BLB-001"
                {...register('code', { required: 'Код конструкции обязателен' })}
              />
              {errors.code && (
                <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="type" className="form-label">
                Тип конструкции *
              </label>
              <select
                id="type"
                className={`form-input ${errors.type ? 'border-red-500' : ''}`}
                {...register('type', { required: 'Тип конструкции обязателен' })}
              >
                {STRUCTURE_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {errors.type && (
                <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
              )}
            </div>
            
            <div className="md:col-span-2">
              <label htmlFor="location" className="form-label">
                Местоположение *
              </label>
              <input
                id="location"
                type="text"
                className={`form-input ${errors.location ? 'border-red-500' : ''}`}
                placeholder="Адрес или описание местоположения"
                {...register('location', { required: 'Местоположение обязательно' })}
              />
              {errors.location && (
                <p className="mt-1 text-sm text-red-600">{errors.location.message}</p>
              )}
            </div>
             
            <div>
                 <label htmlFor="latitude" className="form-label">Широта</label>
                 <input
                     id="latitude"
                     type="number"
                     step="any"
                     className={`form-input ${errors.latitude ? 'border-red-500' : ''}`}
                     placeholder="e.g., 43.2389..."
                     {...register('latitude', { valueAsNumber: true })}
                 />
                 {errors.latitude && <p className="mt-1 text-sm text-red-600">{errors.latitude.message}</p>}
             </div>
             <div>
                 <label htmlFor="longitude" className="form-label">Долгота</label>
                 <input
                     id="longitude"
                     type="number"
                     step="any"
                     className={`form-input ${errors.longitude ? 'border-red-500' : ''}`}
                     placeholder="e.g., 76.8512..."
                     {...register('longitude', { valueAsNumber: true })}
                 />
                 {errors.longitude && <p className="mt-1 text-sm text-red-600">{errors.longitude.message}</p>}
             </div>

             <div className="md:col-span-2">
              <label htmlFor="description" className="form-label">
                Описание
              </label>
              <textarea
                id="description"
                rows={2}
                className={`form-input ${errors.description ? 'border-red-500' : ''}`}
                placeholder="Дополнительное описание (необязательно)"
                {...register('description')}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>
          </fieldset>

            <fieldset className="grid grid-cols-1 gap-6 md:grid-cols-2 border-t pt-6">
                <legend className="text-lg font-medium text-gray-900 mb-2">Технические характеристики</legend>
                <div>
                <label htmlFor="size" className="form-label">Размер</label>
                <input
                    id="size"
                    type="text"
                    className={`form-input ${errors.size ? 'border-red-500' : ''}`}
                    placeholder="Например, 3x6 м"
                    {...register('size')}
                />
                {errors.size && <p className="mt-1 text-sm text-red-600">{errors.size.message}</p>}
                </div>

                 <div>
                    <label htmlFor="direction" className="form-label">Сторона / Направление</label>
                    <input
                        id="direction"
                        type="text"
                        className={`form-input ${errors.direction ? 'border-red-500' : ''}`}
                        placeholder="Например, Сторона А, по ходу движения"
                        {...register('direction')}
                    />
                    {errors.direction && <p className="mt-1 text-sm text-red-600">{errors.direction.message}</p>}
                </div>

                <div className="md:col-span-2 flex items-center space-x-3">
                    <input
                        id="isActive"
                        type="checkbox"
                        className="h-4 w-4 text-primary focus:ring-primary-dark border-gray-300 rounded"
                        {...register('isActive')}
                    />
                     <label htmlFor="isActive" className="form-label mb-0">
                        Конструкция активна (доступна для бронирования)
                    </label>
                </div>

                <div className="md:col-span-2 flex items-center space-x-3">
                    <input
                        id="isLED"
                        type="checkbox"
                        className="h-4 w-4 text-primary focus:ring-primary-dark border-gray-300 rounded"
                        {...register('isLED')}
                        disabled={watch('type')=== 'LED Экран'}
                    />
                    <label htmlFor="isLED" className="form-label mb-0">
                        LED Экран
                    </label>
                </div>

                <div className="md:col-span-2 flex items-center space-x-3">
                    <input
                        id="alwaysAvailable"
                        type="checkbox"
                        className="h-4 w-4 text-primary focus:ring-primary-dark border-gray-300 rounded"
                        {...register('alwaysAvailable')}
                    />
                    <label htmlFor="alwaysAvailable" className="form-label mb-0">
                        Всегда доступно (не требует бронирования)
                    </label>
                </div>
                
                {isLedWatch && (
                    <>
                        <div>
                            <label htmlFor="screenSize" className="form-label">Размер экрана (LED)</label>
                            <input
                                id="screenSize"
                                type="text"
                                className={`form-input ${errors.screenSize ? 'border-red-500' : ''}`}
                                placeholder="Например, 4x8 м"
                                {...register('screenSize')}
                            />
                             {errors.screenSize && <p className="mt-1 text-sm text-red-600">{errors.screenSize.message}</p>}
                        </div>
                        <div>
                            <label htmlFor="screenResolution" className="form-label">Разрешение экрана (LED)</label>
                            <input
                                id="screenResolution"
                                type="text"
                                className={`form-input ${errors.screenResolution ? 'border-red-500' : ''}`}
                                placeholder="Например, 1920x1080 px"
                                {...register('screenResolution')}
                            />
                             {errors.screenResolution && <p className="mt-1 text-sm text-red-600">{errors.screenResolution.message}</p>}
                        </div>
                         <div className="md:col-span-2">
                            <label htmlFor="powerConsumption" className="form-label">Потребляемая мощность (LED)</label>
                            <input
                                id="powerConsumption"
                                type="text"
                                className={`form-input ${errors.powerConsumption ? 'border-red-500' : ''}`}
                                placeholder="Например, 5 кВт"
                                {...register('powerConsumption')}
                            />
                            {errors.powerConsumption && <p className="mt-1 text-sm text-red-600">{errors.powerConsumption.message}</p>}
                        </div>
                    </>
                )}
            </fieldset>

            <fieldset className="border-t pt-6">
                 <legend className="text-lg font-medium text-gray-900 mb-2">Заметки и Изображение</legend>
                <div>
                <label htmlFor="notes" className="form-label">
                    Заметки
                </label>
                <textarea
                    id="notes"
                    rows={3}
                    className={`form-input ${errors.notes ? 'border-red-500' : ''}`}
                    placeholder="Любая дополнительная информация"
                    {...register('notes')}
                />
                {errors.notes && (
                    <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>
                )}
                </div>

                <div className="mt-6">
                     <div className="flex-shrink-0 h-20 w-32 rounded-md border border-gray-200 overflow-hidden flex items-center justify-center bg-gray-100">
                        {imagePreview ? (
                            <img src={imagePreview} alt="Предпросмотр" className="h-full w-full object-cover" />
                        ) : (
                            <CameraIcon className="h-10 w-10 text-gray-400" />
                        )}
                     </div>
                     
                     <label htmlFor="image-upload" className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 inline-flex items-center">
                         <ArrowUpTrayIcon className="-ml-1 mr-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                         {currentImageUrl || imagePreview ? 'Заменить' : 'Загрузить'}
                         <input 
                            id="image-upload" 
                            type="file" 
                            className="sr-only" 
                            accept="image/*" 
                            {...register('file')}
                         />
                     </label>
                     
                     {(imagePreview && imageFileWatch && imageFileWatch instanceof FileList && imageFileWatch.length > 0) && (
                         <button 
                            type="button" 
                            onClick={() => { setValue('file', null); setImagePreview(currentImageUrl); }}
                            className="text-xs text-red-600 hover:text-red-800 ml-2"
                         >
                             Отменить
                         </button>
                     )}
                     {(currentImageUrl && (!imageFileWatch || (imageFileWatch instanceof FileList && imageFileWatch.length === 0))) && (
                          <button 
                            type="button" 
                            onClick={() => { setValue('file', null); setValue('imageUrl', ''); setCurrentImageUrl(null); setImagePreview(null); }}
                            className="text-xs text-red-600 hover:text-red-800 ml-2"
                         >
                             Удалить текущее
                         </button>
                     )}
                 </div>
                 {errors.file && <p className="mt-1 text-sm text-red-600">{errors.file.message}</p>}
             </fieldset>

            {!isEditMode && (
                <>
                    <div className="border-t pt-6 md:col-span-2">
                         <p className="text-base font-medium text-gray-900">Начальная цена (необязательно)</p>
                         <p className="text-sm text-gray-500 mb-4">Укажите дневную и месячную цену, которая будет действовать с момента создания.</p>
                    </div>
                    <div>
                        <label htmlFor="dailyPrice" className="form-label">Цена за день (KZT)</label>
                        <input
                            id="dailyPrice"
                            type="number"
                            step="0.01"
                            min="0"
                            className={`form-input ${errors.dailyPrice ? 'border-red-500' : ''}`}
                            placeholder="Например, 5000"
                            {...register('dailyPrice', { valueAsNumber: true, min: { value: 0, message: 'Цена не может быть отрицательной' } })}
                        />
                        {errors.dailyPrice && <p className="mt-1 text-sm text-red-600">{errors.dailyPrice.message}</p>}
                    </div>
                     <div>
                        <label htmlFor="monthlyPrice" className="form-label">Цена за месяц (KZT)</label>
                        <input
                            id="monthlyPrice"
                            type="number"
                            step="0.01"
                            min="0"
                            className={`form-input ${errors.monthlyPrice ? 'border-red-500' : ''}`}
                            placeholder="Например, 100000"
                            {...register('monthlyPrice', { valueAsNumber: true, min: { value: 0, message: 'Цена не может быть отрицательной' } })}
                        />
                        {errors.monthlyPrice && <p className="mt-1 text-sm text-red-600">{errors.monthlyPrice.message}</p>}
                    </div>
                </>
            )}

          <div className="pt-5 flex justify-end space-x-3 border-t">
            <button 
              type="button" 
              onClick={() => navigate('/structures')} 
              className="btn-secondary"
              disabled={submitting}
            >
              Отмена
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? <LoadingSpinner /> : (isEditMode ? 'Сохранить Изменения' : 'Добавить Конструкцию')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StructureForm;
